import React, { useState, useMemo, useEffect, useRef } from 'react';
import { View, Text, StyleSheet, Animated } from 'react-native';
import { useSelector, useDispatch } from 'react-redux';
import { RootState } from '../../../../../store';
import { setTaskToFavoriteIdMapping } from '../../../../../store/slices/standupRedirectSlice';
import { setToastNotification } from '../../../../../store/slices/actionPlanSlice';
import { useActionPlanDashboard } from '../../../../ActionPlan/hooks/useActionPlanDashboard';
import { useFavorites } from '../../../hooks/useFavorites';
import { useLighthouseTasks } from '../../../../../hooks/useLighthouseTasks';
import { createApiClient, apiClient, getLocalTimestamp, getTimezoneOffsetString } from '../../../../../api/apiClient';
import LighthouseTaskContainer from '../../LighthouseTaskContainer';
import LighthouseTask from '../../LighthouseTask';
import DotPagination from '../../DotPagination';
import TableSection from '../../../../ActionPlan/components/TableSection';
import { StarIconOutline } from '../../StandupIcons';
import FontFamily from '../../../../../constants/fonts';
import logger from '../../../../../utils/logger';
import LHModalPop from '../../../../../components/LHModalPop';
import { getUnfavouriteModalConfig } from '../../../constants/unfavouriteModalConfig';
import { handleStandUpErrorWithFriendlyMessage } from '../../../../../store/utils/standUpErrorHandler';
import { STANDUP_API_NAMES } from '../../../../../store/slices/standUpSlice';

interface KeyActivitiesTabProps {
  favoriteLighthouseTasks: string[];
  onToggleFavoriteLighthouseTask: (taskId: string) => void;
  standupType?: string;
  standupSessionId?: string;
  selectedLocation?: any;
  onAllTasksChange?: (tasks: any[]) => void;
  onContinueToActionPlan?: (task: any) => void;
  onViewActionPlanDetails?: (actionPlan: any) => void;
  favoritesHook?: any;
  lighthouseTasksHook?: any;
  completedLighthouseTaskIds?: string[];
  onRefreshTopOpportunities?: () => Promise<void>;
  onRefreshKeyActivitiesData?: (refreshFn: () => void) => void;
}

export const KeyActivitiesTab: React.FC<KeyActivitiesTabProps> = ({
  favoriteLighthouseTasks,
  onToggleFavoriteLighthouseTask,
  standupType,
  standupSessionId,
  selectedLocation: propSelectedLocation,
  onAllTasksChange,
  onContinueToActionPlan,
  onViewActionPlanDetails,
  favoritesHook,
  lighthouseTasksHook,
  completedLighthouseTaskIds = [],
  onRefreshTopOpportunities,
  onRefreshKeyActivitiesData,
}) => {
  const dispatch = useDispatch();
  const [currentPage, setCurrentPage] = useState(0);
  const [favoriteActionPlans, setFavoriteActionPlans] = useState<string[]>([]);
  const [lighthouseTasksData, setLighthouseTasksData] = useState<any[]>([]);
  const [isLoadingTasks, setIsLoadingTasks] = useState(false);
  
  // Unfavourite confirmation modal state
  const [showUnfavouriteModal, setShowUnfavouriteModal] = useState(false);
  const [pendingUnfavouriteTask, setPendingUnfavouriteTask] = useState<any>(null);
  const [isUnmatchedFavoriteRemoval, setIsUnmatchedFavoriteRemoval] = useState(false);

  // Skeleton component for lighthouse tasks loading state
  const LighthouseTaskSkeleton: React.FC = () => {
    const opacity = useRef(new Animated.Value(0.3)).current;

    useEffect(() => {
      const animation = Animated.loop(
        Animated.sequence([
          Animated.timing(opacity, {
            toValue: 1,
            duration: 800,
            useNativeDriver: true,
          }),
          Animated.timing(opacity, {
            toValue: 0.3,
            duration: 800,
            useNativeDriver: true,
          }),
        ])
      );
      animation.start();
      return () => animation.stop();
    }, [opacity]);

    return (
      <View style={styles.skeletonContainer}>
        <Animated.View style={[styles.skeletonTitle, { opacity }]} />
        <View style={styles.skeletonCards}>
          {[...Array(3)].map((_, index) => (
            <View key={`skeleton-${index}`} style={styles.skeletonCard}>
              <View style={styles.skeletonCardHeader}>
                <Animated.View style={[styles.skeletonCardTitle, { opacity }]} />
                <Animated.View style={[styles.skeletonIcon, { opacity }]} />
              </View>
              <Animated.View style={[styles.skeletonCardSubtitle, { opacity }]} />
              <Animated.View style={[styles.skeletonCardDescription, { opacity }]} />
            </View>
          ))}
        </View>
      </View>
    );
  };

  // Get selected location from props or Redux
  const { selectedLocation: reduxLocation } = useSelector((state: RootState) => state.uiPreferences);
  // Memoize selectedLocation to prevent unnecessary re-renders and duplicate API calls
  const selectedLocation = useMemo(() => {
    return propSelectedLocation || reduxLocation;
  }, [propSelectedLocation?.id, propSelectedLocation?.displayText, reduxLocation?.id, reduxLocation?.displayText]);

  // useActionPlanDashboard may recreate handleRetry frequently; keep the latest in a ref
  // so we can register it with the parent without depending on its identity.
  const handleRetryRef = useRef<null | (() => void)>(null);

  // Get CI Dashboard data from Redux (already fetched by useHomeScreenData)
  const { ciDashboardData } = useSelector((state: RootState) => state.homescreen);

  // Get period and week from Redux
  const { periodWeek } = useSelector((state: RootState) => state.appData);

  // Get user email from Redux
  const userEmail = useSelector(
    (state: RootState) =>
      state.appData?.userInfo?.email ?? state.auth?.user?.email ?? ''
  );

  // Get user info for API calls
  const userGpid = useSelector((state: RootState) => state.appData?.userInfo?.GPID);
  const userRoleId = useSelector((state: RootState) => state.appData?.userInfo?.roleId);

  // Use favorites from Redux (fetched globally at StandUpRedirectScreen level)
  const kfaFavorites = favoritesHook?.kfaFavorites || [];
  const taskToFavoriteIdMap = favoritesHook?.taskToFavoriteIdMap || {};

  // Use the ActionPlan hook for complete functionality
  const {
    actionPlanData,
    isLoading,
    error,
    pagination,
    currentPage: currentTablePage,
    filters,
    filterOptions,
    isFiltersExpanded,
    filterAnimation,
    isMobile,
    isTablet,
    handleFilterChange,
    handleStatusChange,
    handleDeleteSuccess,
    handlePageChange,
    handlePageSizeChange,
    handleRetry,
    toggleFiltersExpansion,
    handleEditActionPlan,
    handleViewActionPlanDetails: defaultHandleViewActionPlanDetails,
    resetFilters,
    getResponsiveItemsPerRow,
  } = useActionPlanDashboard(selectedLocation, { skipCalendarAndFilters: true, sessionId: standupSessionId, excludeIsTempParam: true });

  // Provide parent component with refresh function for synchronization
  useEffect(() => {
    handleRetryRef.current = handleRetry || null;
  }, [handleRetry]);

  // Register refresh function with parent only when context changes (location/session).
  // This avoids a parent<->child loop if handleRetry identity churns.
  useEffect(() => {
    if (!onRefreshKeyActivitiesData) return;
    if (!handleRetryRef.current) return;
    onRefreshKeyActivitiesData(handleRetryRef.current);
  }, [onRefreshKeyActivitiesData, selectedLocation?.id, standupSessionId]);

  // Use custom view details handler if provided, otherwise use default
  const handleViewActionPlanDetailsCustom = (item: any) => {
    if (onViewActionPlanDetails) {
      onViewActionPlanDetails(item);
    } else {
      defaultHandleViewActionPlanDetails(item);
    }
  };

  // Handle favorite toggle for action plans (flag icon in table)
  const handleToggleFavoriteActionPlan = async (itemId: string) => {
    const isCurrentlyFavorite = favoriteActionPlans.includes(itemId);
    const newFavoriteStatus = !isCurrentlyFavorite;

    // Find the action plan item to get its is_temp value
    const actionPlanItem = actionPlanData.find((item: any) => String(item.ID) === itemId);
    const isTempValue = actionPlanItem?.IS_TEMP === 1 || actionPlanItem?.IS_TEMP === true || actionPlanItem?.is_temp === 1 || actionPlanItem?.is_temp === true;

    // Optimistic update - update local state immediately
    setFavoriteActionPlans(prev => {
      if (isCurrentlyFavorite) {
        return prev.filter(id => id !== itemId);
      }
      return [...prev, itemId];
    });

    try {
      // Call PUT API to update top_opportunity flag
      const api = createApiClient();
      const payload = {
        top_opportunity: newFavoriteStatus,
        clientTimestamp: getLocalTimestamp(),
        updated_by: userGpid || '',
        session_id: standupSessionId || '',
        updated_by_email: userEmail || '',
        updated_by_role_id: userRoleId || 0,
        is_temp: isTempValue,
      };

      console.log(`Updating action plan ${itemId} top_opportunity to ${newFavoriteStatus}`);
      console.log('PUT payload:', payload);

      const clientTimezone = getTimezoneOffsetString();
      const response = await apiClient(api, `/action-plans/${itemId}?clientTimezone=${encodeURIComponent(clientTimezone)}`, 'PUT', payload);

      if (response.status === 'success') {
        // Show success toast
        dispatch(setToastNotification({
          header: '',
          subtext: newFavoriteStatus
            ? 'Action plan marked as top opportunity'
            : 'Action plan unmarked as top opportunity',
          variant: 'success',
          position: 'top',
          duration: 3000,
        }));

        console.log('Action plan top_opportunity updated successfully');

        // Refresh both Top Opportunities and Action Plan data to keep tables synchronized
        const refreshPromises = [];

        if (onRefreshTopOpportunities) {
          console.log('Refreshing Top Opportunities section...');
          refreshPromises.push(onRefreshTopOpportunities());
        }

        if (handleRetry) {
          console.log('Refreshing Action Plan data...');
          refreshPromises.push(Promise.resolve(handleRetry()));
        }

        // Wait for both refreshes to complete
        await Promise.all(refreshPromises);
      } else {
        throw new Error(response.message || 'Failed to update action plan');
      }
    } catch (error: any) {
      console.error('Error updating action plan top_opportunity:', error);

      // Revert optimistic update on error
      setFavoriteActionPlans(prev => {
        if (isCurrentlyFavorite) {
          return [...prev, itemId];
        }
        return prev.filter(id => id !== itemId);
      });

      handleStandUpErrorWithFriendlyMessage(
        STANDUP_API_NAMES.UPDATE_ACTION_PLAN_TOP_OPPORTUNITY,
        error,
        true,
        { actionPlanId: itemId, newFavoriteStatus }
      );
    }
  };

  // Use lighthouse tasks from Redux (fetched upfront at redirect screen level)
  // Transform API data into the format expected by the component
  useEffect(() => {
    if (!lighthouseTasksHook && ciDashboardData?.data.length===0) return;
    const { tasks: apiTasks, loading } = lighthouseTasksHook;
    setIsLoadingTasks(loading);

    if (loading || (!apiTasks && ciDashboardData?.data.length === 0) || (apiTasks.length === 0 && ciDashboardData?.data.length === 0)) {
      return;
    }

    try {
      // Transform API data into categories
      const categorizedData: { [key: string]: any[] } = {};

      // Process tasks from API
      apiTasks.forEach((category: any) => {
        const moduleName = category.category;
        
        // Map tasks without filtering by completed status
        const tasks = category.tasks
          .map((task: any) => {
            console.log('Processing task:', {
              id: task.id,
              kfa_id: task.kfa_id,
              kfa_type: task.kfa_type,
              module_name: task.module_name,
              title: task.title
            });
            
            return {
              id: `${moduleName}_${task.kfa_id || task.id}`, // Make ID unique by including module name
              kfa_id: task.kfa_id,
              title: task.title,
              subHeader: task.subHeader,
              description: task.description,
              module_name: moduleName, // Use the category name as module_name
              kfa_type: task.kfa_type || '', // Get kfa_type from individual task
            };
          });
        
        if (tasks.length > 0) {
          categorizedData[moduleName] = tasks;
        }
      });

      // Add Continuous Improvement data from CI Dashboard
      if (ciDashboardData?.data && Array.isArray(ciDashboardData.data)) {
        const ciTasks = ciDashboardData.data
          .filter((item: any) => item.CI_ID != null && item.CI_ID !== '') // Only include items with valid CI_ID
          .map((item: any, index: number) => {
            // Format target end date to MM/DD/YYYY
            let formattedDate = '';
            if (item['Target End Date']) {
              try {
                const date = new Date(item['Target End Date']);
                const month = String(date.getMonth() + 1).padStart(2, '0');
                const day = String(date.getDate()).padStart(2, '0');
                const year = date.getFullYear();
                formattedDate = `${month}/${day}/${year}`;
              } catch {
                formattedDate = item['Target End Date'];
              }
            }

            return {
              id: `ci_${item['CI_ID']}`, // Prefix with 'ci_' to distinguish from lighthouse tasks
              ci_id: item['CI_ID'], // Store original CI_ID for matching with favorites
              kfa_id: item['CI_ID'], // Also store as kfa_id for consistent matching
              title: item['Continuous Improvement Opportunity'] || 'CI Objective',
              subHeader: formattedDate,
              description: item['Solution description'] || '',
              status: item['Status'] || '',
              module_name: 'Continuous Improvement',
              kfa_type: 'Continuous Improvement', // Add kfa_type for consistency
              targetEndDate: item['Target End Date'], // Keep original date for sorting
            };
          })
          .sort((a: any, b: any) => {
            // Sort by Target End Date in ascending order (nearest due date first)
            if (!a.targetEndDate && !b.targetEndDate) return 0;
            if (!a.targetEndDate) return 1; // Items without dates go to the end
            if (!b.targetEndDate) return -1;

            try {
              const dateA = new Date(a.targetEndDate);
              const dateB = new Date(b.targetEndDate);
              return dateA.getTime() - dateB.getTime();
            } catch {
              return 0;
            }
          });

        if (ciTasks.length > 0) {
          categorizedData['Continuous Improvement'] = ciTasks;
        }
      }

      // Convert to array format expected by the component
      const formattedData = Object.entries(categorizedData).map(([category, tasks]) => ({
        category,
        tasks,
      }));

      // Sort categories to ensure Continuous Improvement is first
      formattedData.sort((a, b) => {
        if (a.category === 'Continuous Improvement') return -1;
        if (b.category === 'Continuous Improvement') return 1;
        return a.category.localeCompare(b.category);
      });

      setLighthouseTasksData(formattedData);
    } catch (error) {
      console.error('Error processing lighthouse tasks:', error);
      setLighthouseTasksData([]);
    }
  }, [lighthouseTasksHook?.tasks, lighthouseTasksHook?.loading, ciDashboardData, completedLighthouseTaskIds]);

  // Match favorites with lighthouse tasks after both are loaded
  // This useEffect syncs UI state (favoriteLighthouseTasks) with Redux state (kfaFavorites)
  // IMPORTANT: favoriteLighthouseTasks is intentionally NOT in the dependency array to prevent
  // race conditions between optimistic UI updates and Redux synchronization
  useEffect(() => {
    // Skip only if lighthouse tasks data is not loaded yet
    // DO NOT skip when kfaFavorites is empty - we need to clear all favorites in that case
    if (lighthouseTasksData.length === 0) {
      console.log('Skipping favorites matching - lighthouse tasks not loaded', {
        favoritesCount: kfaFavorites.length,
        tasksCount: lighthouseTasksData.length
      });
      return;
    }

    console.log('=== Matching KFA favorites with lighthouse tasks ===');
    console.log('KFA favorites from Redux:', kfaFavorites);
    console.log('Lighthouse tasks data:', lighthouseTasksData);
    console.log('CI Dashboard data:', ciDashboardData);

    // Get all lighthouse tasks as flat array
    const allTasks = lighthouseTasksData.flatMap(category => category.tasks);
    console.log('All lighthouse tasks:', allTasks);

    // Get CI Dashboard data for Continuous Improvement matching
    const ciData = ciDashboardData?.data || [];
    console.log('CI data available:', ciData.length, 'items');

    // Match each favorite with its corresponding task or CI item
    const matchedTaskIds: string[] = [];

    kfaFavorites.forEach((favorite: any) => {
      console.log('Processing favorite:', favorite);

      const itemId = String(favorite.item_id || '');
      const itemName = favorite.item_name || favorite.description || '';
      const moduleName = favorite.module_name || favorite.title || '';

      console.log('Favorite details:', {
        itemId,
        itemName,
        moduleName,
        item_type: favorite.item_type
      });

      // Check if this is a Continuous Improvement item
      const isContinuousImprovement = 
        moduleName.toLowerCase().includes('continuous improvement') ||
        moduleName.toLowerCase().includes('continuousimprovement') ||
        moduleName.toLowerCase() === 'continuous improvement';

      if (isContinuousImprovement) {
        console.log('This is a Continuous Improvement favorite, matching with lighthouse tasks CI data...');
        
        // Match with CI tasks in lighthouse data (already transformed)
        const matchedCiTask = allTasks.find((task: any) => {
          const taskCiId = String(task.ci_id || '');
          const match = taskCiId === itemId && taskCiId !== '' && taskCiId !== 'null';
          if (match) {
            console.log('✓ CI task matched:', task);
          }
          return match;
        });

        if (matchedCiTask) {
          console.log('Successfully matched CI task:', matchedCiTask);
          // Use the task's ID (which is 'ci_${CI_ID}')
          matchedTaskIds.push(matchedCiTask.id);
          
          // Store mapping for CI items
          if (favorite.favourite_id) {
            dispatch(setTaskToFavoriteIdMapping({
              taskId: matchedCiTask.id,
              favoriteId: favorite.favourite_id
            }));
          }
        } else {
          console.log('No matching CI task found for favorite:', favorite);
        }
      } else {
        // Match with lighthouse tasks
        console.log('Matching with lighthouse tasks...');

        // Find matching task by kfa_id and module_name
        const matchingTask = allTasks.find((task: any) => {
          const taskKfaId = String(task.kfa_id || '');
          const taskModuleName = String(task.module_name || '');
          const taskKfaType = String(task.kfa_type || '');

          console.log('Checking task:', {
            taskId: task.id,
            taskKfaId,
            taskModuleName,
            taskKfaType,
            favoriteItemId: itemId,
            favoriteModuleName: moduleName
          });

          // Must match by kfa_id first
          const idMatch = taskKfaId === itemId;
          if (!idMatch) return false;

          // If we have module information in favorite, validate it matches
          if (moduleName) {
            // Match module_name directly or check for compound module_name format
            let moduleMatch = false;
            
            if (moduleName.includes('_')) {
              // Format: "ModuleName_KfaType" - match both parts
              const [expectedModuleName, expectedKfaType] = moduleName.split('_');
              moduleMatch = 
                taskModuleName.toLowerCase() === expectedModuleName.toLowerCase() &&
                taskKfaType.toLowerCase() === expectedKfaType.toLowerCase();
              
              console.log('Compound module matching:', {
                expectedModuleName,
                expectedKfaType,
                taskModuleName,
                taskKfaType,
                moduleMatch
              });
            } else {
              // Simple module name - match against module_name or kfa_type
              moduleMatch = 
                taskModuleName.toLowerCase() === moduleName.toLowerCase() ||
                taskKfaType.toLowerCase() === moduleName.toLowerCase();
              
              console.log('Simple module matching:', {
                moduleName,
                taskModuleName,
                taskKfaType,
                moduleMatch
              });
            }

            if (moduleMatch) {
              console.log('✓ Task matched by ID and module:', {
                taskId: task.id,
                taskKfaId,
                taskModuleName,
                taskKfaType
              });
            }
            
            return moduleMatch;
          }

          // If no module_name in favorite, match by ID only (fallback)
          console.log('✓ Task matched by ID only:', taskKfaId);
          return true;
        });

        if (matchingTask) {
          console.log('Found matching lighthouse task:', matchingTask);
          matchedTaskIds.push(matchingTask.id);

          // Populate the taskToFavoriteIdMap in Redux for unfavoriting later
          if (favorite.favourite_id) {
            console.log('Storing favorite mapping:', {
              taskId: matchingTask.id,
              favouriteId: favorite.favourite_id
            });
            dispatch(setTaskToFavoriteIdMapping({
              taskId: matchingTask.id,
              favoriteId: favorite.favourite_id
            }));
          }
        } else {
          console.log('No matching lighthouse task found for favorite:', favorite);
        }
      }
    });

    console.log('Matched task IDs:', matchedTaskIds);

    // Calculate which tasks should be favorited vs currently favorited
    const currentlyFavorited = favoriteLighthouseTasks;
    const shouldBeFavorited = matchedTaskIds;

    console.log('Should be favorited:', shouldBeFavorited);
    console.log('Currently favorited:', currentlyFavorited);

    // Add tasks that should be favorited but aren't yet
    const toAdd = shouldBeFavorited.filter(taskId => !currentlyFavorited.includes(taskId));
    
    // Remove tasks that are currently favorited but shouldn't be
    // Only consider lighthouse tasks (not unmatched favorites)
    const allLighthouseTaskIds = lighthouseTasksData.flatMap(category => 
      category.tasks.map((task: any) => task.id)
    );
    const toRemove = currentlyFavorited.filter(taskId => 
      !shouldBeFavorited.includes(taskId) && 
      allLighthouseTaskIds.includes(taskId) // Only remove lighthouse tasks
    );

    console.log('Tasks to add to favorites:', toAdd);
    console.log('Tasks to remove from favorites:', toRemove);

    // Apply additions
    if (toAdd.length > 0) {
      toAdd.forEach(taskId => {
        console.log('Adding task to favorites:', taskId);
        onToggleFavoriteLighthouseTask(taskId);
      });
    }

    // Apply removals
    if (toRemove.length > 0) {
      toRemove.forEach(taskId => {
        console.log('Removing task from favorites:', taskId);
        onToggleFavoriteLighthouseTask(taskId);
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [kfaFavorites, lighthouseTasksData, ciDashboardData, dispatch]);

  // Use API data only. If no categories returned, default to empty array.
  const lighthouseTasksDataSafe = lighthouseTasksData && lighthouseTasksData.length > 0 ? lighthouseTasksData : [];

  // Get all tasks as flat array for favorites lookup
  const allTasks = useMemo(() => {
    const tasks = lighthouseTasksData.flatMap(category =>
      category.tasks.map((task: any) => ({
        ...task,
        category: category.category,
        isFavorite: favoriteLighthouseTasks.includes(task.id),
      }))
    );
    console.log('allTasks computed:', tasks.length, 'tasks');
    return tasks;
  }, [lighthouseTasksData, favoriteLighthouseTasks]);

  // Notify parent when allTasks changes
  useEffect(() => {
    if (onAllTasksChange) {
      onAllTasksChange(allTasks);
    }
  }, [allTasks, onAllTasksChange]);

  // Get selected/favorite tasks - calculate everything directly from Redux kfaFavorites
  const selectedTasks = useMemo(() => {
    if (kfaFavorites.length === 0) {
      console.log('No KFA favorites in Redux state');
      return [];
    }

    console.log('=== Calculating selectedTasks from Redux kfaFavorites ===');
    console.log('KFA favorites:', kfaFavorites);
    console.log('All lighthouse tasks:', allTasks.length);
    console.log('CI Dashboard data:', ciDashboardData?.data?.length || 0);

    const result: any[] = [];
    const ciData = ciDashboardData?.data || [];

    kfaFavorites.forEach((favorite: any) => {
      const itemId = String(favorite.item_id || '');
      const itemName = favorite.item_name || '';
      const moduleName = favorite.module_name || '';

      console.log('Processing favorite for selectedTasks:', {
        favourite_id: favorite.favourite_id,
        itemId,
        itemName,
        moduleName
      });

      // Check if this is a Continuous Improvement item
      const isContinuousImprovement = 
        moduleName.toLowerCase().includes('continuous improvement') ||
        moduleName.toLowerCase().includes('continuousimprovement') ||
        moduleName.toLowerCase() === 'continuous improvement';

      let matchedTask = null;

      if (isContinuousImprovement) {
        // Look for matching CI task
        matchedTask = allTasks.find((task: any) => {
          const taskCiId = String(task.ci_id || '');
          return taskCiId === itemId && taskCiId !== '' && taskCiId !== 'null';
        });

        if (matchedTask && matchedTask.isFavorite) {
          console.log('Found matching CI task in allTasks:', matchedTask);
          result.push(matchedTask);
        }
      } else {
        // Look for matching lighthouse task using improved matching logic
        matchedTask = allTasks.find((task: any) => {
          const taskKfaId = String(task.kfa_id || '');
          const taskModuleName = String(task.module_name || '');
          const taskKfaType = String(task.kfa_type || '');
          
          // Must match by kfa_id first
          const idMatch = taskKfaId === itemId;
          if (!idMatch) return false;

          // If we have module information in favorite, validate it matches
          if (moduleName) {
            let moduleMatch = false;
            
            if (moduleName.includes('_')) {
              // Format: "ModuleName_KfaType" - match both parts exactly
              const [expectedModuleName, expectedKfaType] = moduleName.split('_');
              moduleMatch = 
                taskModuleName.toLowerCase() === expectedModuleName.toLowerCase() &&
                taskKfaType.toLowerCase() === expectedKfaType.toLowerCase();
            } else {
              // Simple module name - match against module_name or kfa_type
              moduleMatch = 
                taskModuleName.toLowerCase() === moduleName.toLowerCase() ||
                taskKfaType.toLowerCase() === moduleName.toLowerCase();
            }
            
            return moduleMatch;
          }

          return true; // Fallback to ID match only
        });

        if (matchedTask && matchedTask.isFavorite) {
          console.log('Found matching lighthouse task in allTasks:', matchedTask);
          result.push(matchedTask);
        }
      }

      // If no matching task found, create unmatched task from Redux favorite
      if (!matchedTask) {
        console.log('No matching task found, creating unmatched task from favorite:', favorite);
        const unmatchedTask = {
          id: `unmatched_${favorite.favourite_id}`,
          kfa_id: favorite.item_id,
          title: favorite.module_name || 'Unknown Module',
          subHeader: '',
          description: favorite.item_name || '',
          module_name: favorite.module_name || '',
          category: isContinuousImprovement ? 'Continuous Improvement' : (favorite.module_name || 'Unknown'),
          isFavorite: true,
          isUnmatched: true,
          favourite_id: favorite.favourite_id,
        };
        result.push(unmatchedTask);
      }
    });

    console.log('Final selectedTasks result:', {
      count: result.length,
      tasks: result.map(t => ({ id: t.id, title: t.title, isUnmatched: t.isUnmatched || false }))
    });
    console.log('======================================');

    return result;
  }, [kfaFavorites, allTasks, ciDashboardData]);

  console.log('Selected tasks count:', selectedTasks.length);

  const handleToggleFavorite = (taskId: string) => {
    console.log('=== handleToggleFavorite called ===');
    console.log('Task ID:', taskId);
    console.log('All tasks available:', allTasks.length);

    // Check if this is an unmatched favorite
    if (taskId.startsWith('unmatched_')) {
      console.log('This is an unmatched favorite, showing confirmation modal...');
      
      // Find the unmatched favorite in selectedTasks
      const unmatchedTask = selectedTasks.find(t => t.id === taskId && t.isUnmatched);
      if (!unmatchedTask || !unmatchedTask.favourite_id) {
        console.error('Unmatched task or favourite_id not found:', taskId);
        return;
      }

      // Show confirmation modal for unmatched favorites
      setPendingUnfavouriteTask(unmatchedTask);
      setIsUnmatchedFavoriteRemoval(true);
      setShowUnfavouriteModal(true);
      
      return;
    }

    // Find the task details in allTasks
    const task = allTasks.find(t => t.id === taskId);
    if (!task) {
      console.error('Task not found:', taskId);
      return;
    }

    const isFavorited = favoriteLighthouseTasks.includes(taskId);

    console.log('Toggle favorite for task:', {
      taskId,
      isFavorited,
      task,
      kfa_id: task.kfa_id,
      description: task.description
    });

    if (!isFavorited) {
      // Adding favorite - immediate action, no modal
      console.log('Calling onToggleFavoriteLighthouseTask...');
      onToggleFavoriteLighthouseTask(taskId);
      console.log('onToggleFavoriteLighthouseTask called');

      if (favoritesHook?.addFavorite) {
        console.log('Task is not currently favorited, will call addFavorite', task);
        // Construct module_name as "ModuleName_KfaType" for proper API payload
        const moduleName = task.module_name || task.category || '';
        const kfaType = task.title || '';
        const combinedModuleName = kfaType ? `${moduleName}_${kfaType}` : moduleName;
        
        console.log('Adding favorite with combined module_name:', combinedModuleName);
        
        favoritesHook.addFavorite(
          taskId,
          task.kfa_id?.toString() || task.id?.toString() || '',
          task.description || task.title || '',
          combinedModuleName
        );
      }
    } else {
      // Removing favorite - show confirmation modal first
      console.log('Task is already favorited, showing unfavourite modal');
      setPendingUnfavouriteTask(task);
      setIsUnmatchedFavoriteRemoval(false);
      setShowUnfavouriteModal(true);
    }
  };

  // Handle confirmed unfavourite action
  const handleConfirmUnfavourite = async () => {
    if (!pendingUnfavouriteTask) return;

    const taskId = pendingUnfavouriteTask.id;
    console.log('Confirmed unfavourite for task:', taskId);

    // Handle unmatched favorites differently
    if (isUnmatchedFavoriteRemoval) {
      console.log('Removing unmatched favorite via favoritesHook...');
      // Remove from backend using favoritesHook
      // This will call the API and trigger Redux refresh automatically
      if (favoritesHook?.removeFavorite) {
        await favoritesHook.removeFavorite(
          taskId,
          pendingUnfavouriteTask.kfa_id,
          pendingUnfavouriteTask.description,
          pendingUnfavouriteTask.module_name
        );
      }
    } else {
      // Optimistically update UI state immediately for better UX
      onToggleFavoriteLighthouseTask(taskId);

      // Call API to remove favorite and update Redux
      if (favoritesHook?.removeFavorite) {
        // Construct module_name as "ModuleName_KfaType" for proper API payload
        const moduleName = pendingUnfavouriteTask.module_name || pendingUnfavouriteTask.category || '';
        const kfaType = pendingUnfavouriteTask.title || '';
        const combinedModuleName = kfaType ? `${moduleName}_${kfaType}` : moduleName;
        
        console.log('Removing favorite with combined module_name:', combinedModuleName);
        console.log('Calling removeFavorite with taskId:', taskId);
        
        const result = await favoritesHook.removeFavorite(
          taskId,
          pendingUnfavouriteTask.kfa_id?.toString() || pendingUnfavouriteTask.id?.toString() || '',
          pendingUnfavouriteTask.description || pendingUnfavouriteTask.title || '',
          combinedModuleName
        );
        console.log('removeFavorite returned:', result);
        
        // Redux will update kfaFavorites, and the useEffect will ensure sync is maintained
        // By keeping favoriteLighthouseTasks out of the effect's deps, we avoid race conditions
      } else {
        console.error('favoritesHook.removeFavorite is not available!');
      }
    }

    // Close modal and reset
    setShowUnfavouriteModal(false);
    setPendingUnfavouriteTask(null);
    setIsUnmatchedFavoriteRemoval(false);
  };

  // Handle cancel unfavourite
  const handleCancelUnfavourite = () => {
    console.log('Cancelled unfavourite');
    setShowUnfavouriteModal(false);
    setPendingUnfavouriteTask(null);
    setIsUnmatchedFavoriteRemoval(false);
  };

  // Handle favorite/unfavorite action with proper parameter handling
  // This is used by tasks to directly trigger add/remove without showing confirmation
  // (confirmation should be handled by the caller, e.g., LighthouseTaskModal)
  const handleFavoriteAction = async (taskId: string, shouldBeFavorited: boolean) => {
    console.log('=== handleFavoriteAction called ===');
    console.log('Task ID:', taskId);
    console.log('Should be favorited:', shouldBeFavorited);

    // Check if this is an unmatched favorite
    if (taskId.startsWith('unmatched_')) {
      console.log('This is an unmatched favorite, handling removal...');
      const unmatchedTask = selectedTasks.find(t => t.id === taskId && t.isUnmatched);
      if (!unmatchedTask || !unmatchedTask.favourite_id) {
        console.error('Unmatched task or favourite_id not found:', taskId);
        return;
      }

      if (favoritesHook?.removeFavorite) {
        favoritesHook.removeFavorite(taskId, unmatchedTask.kfa_id, unmatchedTask.description, unmatchedTask.module_name);
      }
      return;
    }

    // Find the task details in allTasks
    const task = allTasks.find(t => t.id === taskId);
    if (!task) {
      console.error('Task not found:', taskId);
      return;
    }

    // Update UI state
    onToggleFavoriteLighthouseTask(taskId);

    if (shouldBeFavorited) {
      // Adding favorite
      console.log('Adding favorite for task:', taskId);
      if (favoritesHook?.addFavorite) {
        const moduleName = task.module_name || task.category || '';
        const kfaType = task.title || '';
        const combinedModuleName = kfaType ? `${moduleName}_${kfaType}` : moduleName;
        
        console.log('Adding favorite with combined module_name:', combinedModuleName);
        await favoritesHook.addFavorite(
          taskId,
          task.kfa_id?.toString() || task.id?.toString() || '',
          task.description || task.title || '',
          combinedModuleName
        );
      }
    } else {
      // Removing favorite
      console.log('Removing favorite for task:', taskId);
      if (favoritesHook?.removeFavorite) {
        const moduleName = task.module_name || task.category || '';
        const kfaType = task.title || '';
        const combinedModuleName = kfaType ? `${moduleName}_${kfaType}` : moduleName;
        
        console.log('Removing favorite with combined module_name:', combinedModuleName);
        const result = await favoritesHook.removeFavorite(
          taskId,
          task.kfa_id?.toString() || task.id?.toString() || '',
          task.description || task.title || '',
          combinedModuleName
        );
        console.log('removeFavorite returned:', result);
      }
    }
  };

  // Paginate: 3 categories per page consistently
  const categoriesPerPage = 3;
  const startIndex = currentPage * categoriesPerPage;
  const endIndex = startIndex + categoriesPerPage;
  const displayedCategories = lighthouseTasksDataSafe.slice(startIndex, endIndex);
  const totalPages = Math.ceil(lighthouseTasksDataSafe.length / categoriesPerPage);
  
  // Sync favoriteActionPlans state with actual TOP_OPPORTUNITY field from API data
  useEffect(() => {
    if (actionPlanData && actionPlanData.length > 0) {
      const apiBasedFavorites = actionPlanData
        .filter((item: any) => {
          const topOpportunity = item.TOP_OPPORTUNITY || item.top_opportunity || item.Top_Opportunity || 0;
          return topOpportunity === 1 || topOpportunity === true || topOpportunity === '1';
        })
        .map((item: any) => String(item.ID));

      setFavoriteActionPlans(apiBasedFavorites);
      console.log('Synchronized favoriteActionPlans with API data:', apiBasedFavorites);
    }
  }, [actionPlanData]);

  // Filter out completed and top opportunity (favorited) action plans from TableSection
  const filteredActionPlanData = useMemo(() => {
    return actionPlanData.filter((item: any) => {
      const status = item.STATUS || item.status || item.Status || '';
      const statusLower = status.toString().toLowerCase();
      const isCompleted = statusLower === 'completed';

      // Check if item is marked as top opportunity (favorited)
      const topOpportunity = item.TOP_OPPORTUNITY || item.top_opportunity || item.Top_Opportunity || 0;
      const isTopOpportunity = topOpportunity === 1 || topOpportunity === true || topOpportunity === '1';

      // Check for frontline employee involvement for daily_flt
      const frontlineEmployeeInvolvement = item.FRONTLINE_EMPLOYEE_INVOLVEMENT;
      const shouldExcludeFrontlineInvolvement = (standupType === 'daily_flt' || standupType === 'daily-frontline') && frontlineEmployeeInvolvement === true;

      // Exclude completed, top opportunity items, and frontline employee involvement for daily_flt
      return !isCompleted && !isTopOpportunity && !shouldExcludeFrontlineInvolvement;
    });
  }, [actionPlanData, standupType]);

  const modalConfig = getUnfavouriteModalConfig('key-activities');

  return (
    <View>
      {/* Unfavourite Confirmation Modal */}
      <LHModalPop
        visible={showUnfavouriteModal}
        onClose={handleCancelUnfavourite}
        title={modalConfig.header}
        showFooter={true}
        primaryButtonText={modalConfig.primaryButton}
        secondaryButtonText={modalConfig.secondaryButton}
        onPrimaryPress={handleConfirmUnfavourite}
        onSecondaryPress={handleCancelUnfavourite}
        showCloseButton={true}
      >
        <View style={{ gap: 12, paddingVertical: 8 }}>
          <Text style={{ fontSize: 14, color: '#3A3A3A', fontFamily: FontFamily.regular, lineHeight: 20 }}>
            {modalConfig.bodyLine1}
          </Text>
          {/* Only show bodyLine2 for normal favorites, not for unmatched favorites */}
          {!isUnmatchedFavoriteRemoval && (
            <Text style={{ fontSize: 14, color: '#3A3A3A', fontFamily: FontFamily.regular, lineHeight: 20 }}>
              {modalConfig.bodyLine2}
            </Text>
          )}
        </View>
      </LHModalPop>

    <View style={styles.LighthouseSelectContainer}>
      {/* Selected Tasks Section - Show selected cards or empty state */}
      {selectedTasks.length === 0 ? (
        <View style={styles.keyActivitiesSection}>
          <View style={styles.keyActivitiesCard}>
            <View style={styles.emptyStateContainer}>
              <StarIconOutline />
              <Text style={styles.emptyStateTitle}>Select Tasks</Text>
              <Text style={styles.emptyStateDescription}>
                Select Lighthouse Tasks to Discuss With Your Team
              </Text>
            </View>
          </View>
        </View>
      ) : (
        <View style={styles.keyActivitiesSection}>
          <View style={styles.selectedTasksContainer}>
            <View style={styles.selectedTasksRow}>
              {selectedTasks.map((task) => (
                <View key={`selected-${task.id}`} style={styles.selectedTaskWrapper}>
                  <LighthouseTask
                    title={task.title}
                    subHeader={task.subHeader}
                    description={task.description}
                    status={task.status}
                    isFavorite={true}
                    disableFavorite={false}
                    onFavoritePress={(isFavorite: boolean) => {
                      if (isFavorite) {
                        // Favoriting - shouldn't happen for selected tasks, but handle it
                        handleToggleFavorite(task.id);
                      } else {
                        // Unfavoriting - for main view, show confirmation
                        handleToggleFavorite(task.id);
                      }
                    }}
                    width="100%"
                  />
                </View>
              ))}
            </View>
          </View>
        </View>
      )}

      {/* Lighthouse Tasks Section */}
      <View style={styles.lighthouseTasksSection}>
        <Text style={styles.lighthouseTasksTitle}>Lighthouse tasks</Text>
        {isLoadingTasks ? (
          <LighthouseTaskSkeleton />
        ) : displayedCategories.length === 0 ? (
          <View style={styles.emptyTasksContainer}>
            <Text style={styles.emptyTasksText}>No lighthouse tasks available</Text>
          </View>
        ) : (
          <View style={styles.lighthouseTasksGrid}>
          {displayedCategories.map((categoryData) => {
            // Filter out completed tasks only for Lighthouse tasks section display
            const tasksForDisplay = categoryData.tasks.filter((task: any) => {
              const taskId = task.id || `${task.module_name}-${task.title}`;
              const isCompleted = completedLighthouseTaskIds.includes(taskId);
              if (isCompleted) {
                console.log('[KeyActivitiesTab] Hiding completed task from Lighthouse tasks section:');
                console.log('  - Task ID:', taskId);
                console.log('  - Task:', task);
              }
              return !isCompleted;
            });
            
            // Only render container if there are tasks to display
            if (tasksForDisplay.length === 0) return null;
            
            return (
              <View key={categoryData.category} style={styles.lighthouseTaskColumn}>
                <LighthouseTaskContainer
                  title={categoryData.category}
                  items={tasksForDisplay.map((task: any) => ({
                    ...task,
                    isFavorite: favoriteLighthouseTasks.includes(task.id),
                    onFavoritePress: () => handleToggleFavorite(task.id),
                  }))}
                  height={400}
                  onContinueToActionPlan={onContinueToActionPlan}
                  onFavoriteActionDirect={handleFavoriteAction}
                />
              </View>
            );
          })}
          </View>
        )}

        {/* Pagination */}
        {!isLoadingTasks && displayedCategories.length > 0 && totalPages > 1 && (
          <View style={styles.paginationContainer}>
            <DotPagination
              totalDots={totalPages}
              activeDot={currentPage}
              onDotPress={setCurrentPage}
            />
          </View>
        )}
      </View>

      {/* TableSection - Action Plan with full functionality from ActionPlanDashboard */}
      <View style={styles.actionPlanTableSection}>
        <TableSection
          actionPlanData={filteredActionPlanData}
          isLoading={isLoading}
          error={error}
          title="Action Plan"
          onEditPress={handleEditActionPlan}
          onViewDetailsPress={handleViewActionPlanDetailsCustom}
          onDeleteSuccess={handleDeleteSuccess}
          onStatusChange={handleStatusChange}
          filterOptions={filterOptions}
          onFilterChange={handleFilterChange}
          currentFilters={filters}
          isMobile={isMobile}
          isTablet={isTablet}
          getResponsiveItemsPerRow={getResponsiveItemsPerRow}
          isFiltersExpanded={isFiltersExpanded}
          filterAnimation={filterAnimation}
          onToggleFilters={toggleFiltersExpansion}
          onClearAllFilters={resetFilters}
          currentPage={currentTablePage}
          totalPages={pagination?.totalPages || 1}
          pageSize={filters.pageSize}
          onPageChange={handlePageChange}
          onPageSizeChange={handlePageSizeChange}
          isHybridView={false}
          showFavoriteIcon={true}
          onFavoriteToggle={handleToggleFavoriteActionPlan}
          favoriteActionPlans={favoriteActionPlans}
          hideActionMenu={true}
          alwaysShowPagination={true}
        />
      </View>
    </View>
    </View>
  );
};

const styles = StyleSheet.create({
  LighthouseSelectContainer: {
    // backgroundColor: '#F7FAFC',
    borderRadius: 8,
    // paddingHorizontal: 16,
    paddingVertical: 16,
    // marginTop:24
  },
  keyActivitiesSection: {
    backgroundColor: '#FFFFFF',
    borderRadius: 8,
    marginLeft: 16,
    marginRight: 16,
    // marginTop: 20,
    // marginBottom: 20,
    paddingHorizontal: 16,
    paddingVertical: 20,
  },
  keyActivitiesCard: {
    // backgroundColor: '#F7FAFC',
    borderRadius: 12,
    padding: 20,
    minHeight: 200,
  },
  emptyStateContainer: {
    justifyContent: 'center',
    alignItems: 'center',
    paddingTop: 26,
  },
  emptyStateTitle: {
    fontSize: 18,
    fontWeight: '700',
    color: '#3A3A3A',
    fontFamily: FontFamily.bold,
    marginTop: 16,
    marginBottom: 8,
  },
  emptyStateDescription: {
    fontSize: 14,
    fontWeight: '400',
    color: '#616161',
    fontFamily: FontFamily.regular,
    textAlign: 'center',
    paddingTop: 8,
  },
  selectedTasksContainer: {
    gap: 12,
  },
  selectedTasksRow: {
    flexDirection: 'row',
    gap: 12,
    flexWrap: 'wrap',
  },
  selectedTaskWrapper: {
    flexBasis: 'calc(25% - 9px)' as any,
    minWidth: 0,
    maxWidth: 'calc(25% - 9px)' as any,
  },
  lighthouseTasksSection: {
    backgroundColor: '#FFFFFF',
    borderRadius: 8,
    paddingHorizontal: 24,
    paddingVertical: 12,
    marginBottom: 20,
    marginTop: 20,
  },
  lighthouseTasksTitle: {
    fontSize: 24,
    fontWeight: '700',
    color: '#2D2D2D',
    fontFamily: FontFamily.bold,
    marginBottom: 16,
    paddingVertical: 8,
    marginLeft: 8,
  },
  lighthouseTasksGrid: {
    flexDirection: 'row',
    gap: 16,
    marginBottom: 16,
  },
  lighthouseTaskColumn: {
    width: 'calc(33.333% - 11px)' as any,
    minWidth: 0,
  },
  paginationContainer: {
    alignItems: 'center',
    marginTop: 8,
  },
  actionPlanTableSection: {
    // padding: 24,
    marginTop: 24,
  },
  emptyTasksContainer: {
    paddingVertical: 60,
    alignItems: 'center',
    justifyContent: 'center',
  },
  emptyTasksText: {
    fontSize: 14,
    color: '#9E9E9E',
    fontFamily: FontFamily.regular,
  },
  skeletonContainer: {
    gap: 20,
  },
  skeletonCards: {
    flexDirection: 'row',
    gap: 16,
  },
  skeletonCard: {
    flex: 1,
    backgroundColor: '#F7FAFC',
    borderRadius: 12,
    padding: 16,
    gap: 12,
    minHeight: 400,
  },
  skeletonCardHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  skeletonCardTitle: {
    width: '70%',
    height: 20,
    backgroundColor: '#E0E0E0',
    borderRadius: 4,
  },
  skeletonIcon: {
    width: 24,
    height: 24,
    backgroundColor: '#E0E0E0',
    borderRadius: 12,
  },
  skeletonCardSubtitle: {
    width: '40%',
    height: 16,
    backgroundColor: '#E0E0E0',
    borderRadius: 4,
  },
  skeletonCardDescription: {
    width: '100%',
    height: 80,
    backgroundColor: '#E0E0E0',
    borderRadius: 4,
  },
  skeletonTitle: {
    width: '30%',
    height: 24,
    backgroundColor: '#E0E0E0',
    borderRadius: 4,
    marginBottom: 8,
  },
});
